import * as React from 'react';

import { useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';

import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Unstable_Grid2';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import EwpFab from '../components/EwpFab';
import Filter from '../components/Filter';
import Header from '../components/Header';
import Subheader from '../components/Subheader';
import FormFileUpload from '../components/form/FormFileUpload';

import { useAuth } from '../context/AuthProvider';
import { useConfirmationDialog } from '../context/ConfirmationDialogContext';
import { useMessage } from '../context/MessageProvider';
import { useConnectivity } from '../context/ConnectivityProvider';

import { listBehaviorTagSets, deleteBehaviorTagSet, saveBehaviorTagSet } from '../models/behaviorTags';
import { downloadCsv } from '../utils/download';
import { schema } from '../models/biologySchema';
import { sectionBBehaviors } from '../utils/sectionBBehaviors';

export async function loader() {
  const behaviorTagSets = await listBehaviorTagSets();
  return { behaviorTagSets };
}

const resolveSpeciesLabel = (speciesValue) => {
  if (!speciesValue) return '';

  for (const phylum of schema) {
    for (const classes of phylum.class || []) {
      for (const order of classes.order || []) {
        for (const genus of order.genus || []) {
          for (const species of genus.species || []) {
            if (species.scientificName === speciesValue) {
              return `${species.commonName} (${species.scientificName})`;
            }
          }
        }
      }
    }
  }

  return speciesValue;
};

const allSpecies = schema.flatMap((phylum) =>
  (phylum.class || []).flatMap((classes) =>
    (classes.order || []).flatMap((order) =>
      (order.genus || []).flatMap((genus) =>
        (genus.species || []).map(
          (species) => `${species.commonName} (${species.scientificName})`
        )
      )
    )
  )
);

const behaviorTitleByKey = sectionBBehaviors.reduce((acc, cur) => {
  acc[cur.behaviorKey] = cur.title;
  return acc;
}, {});

const requiredImportHeaders = ['species', 'behavior', 'tag_label', 'tag_description'];

const speciesValueByLabel = allSpecies.reduce((acc, label) => {
  const scientificName = label.match(/\(([^()]+)\)$/)?.[1] || '';
  acc[label] = scientificName;
  return acc;
}, {});

const validBehaviorKeys = new Set(sectionBBehaviors.map((behavior) => behavior.behaviorKey));

const parseCsvText = (csvText) => {
  const rows = [];
  let currentCell = '';
  let currentRow = [];
  let inQuotes = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index];
    const nextChar = csvText[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentCell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      currentRow.push(currentCell);
      currentCell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }

      currentRow.push(currentCell);
      const normalizedRow = currentRow.map((value) => value.trim());
      if (normalizedRow.some((value) => value !== '')) {
        rows.push(normalizedRow);
      }

      currentCell = '';
      currentRow = [];
      continue;
    }

    currentCell += char;
  }

  if (inQuotes) {
    throw new Error('The CSV file could not be parsed because it contains an unclosed quoted value.');
  }

  if (currentCell !== '' || currentRow.length > 0) {
    currentRow.push(currentCell);
    const normalizedRow = currentRow.map((value) => value.trim());
    if (normalizedRow.some((value) => value !== '')) {
      rows.push(normalizedRow);
    }
  }

  return rows;
};

export default function BehaviorTagSetsListPage({ title = 'Specific Behavior Tags', ...props }) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { behaviorTagSets } = useLoaderData();
  const { facilityId } = useAuth();
  const { confirm } = useConfirmationDialog();
  const { setSuccess, setError } = useMessage();
  const { isOnline } = useConnectivity();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedBehaviorTagSet, setSelectedBehaviorTagSet] = React.useState(null);
  const [importFile, setImportFile] = React.useState(null);
  const [isImportExportOpen, setIsImportExportOpen] = React.useState(false);
  const [filteredBehaviorTagSets, setFilteredBehaviorTagSets] = React.useState(behaviorTagSets);

  const speciesOptions = React.useMemo(() => {
    const values = Array.from(
      new Set((behaviorTagSets || []).map((cur) => resolveSpeciesLabel(cur.species)).filter(Boolean))
    );
    values.sort((a, b) => a.localeCompare(b));
    return values.map((value) => ({ value, label: value }));
  }, [behaviorTagSets]);

  const behaviorOptions = React.useMemo(() => {
    const values = Array.from(
      new Set((behaviorTagSets || []).map((cur) => cur.behavior_key).filter(Boolean))
    );
    values.sort((a, b) => {
      const labelA = behaviorTitleByKey[a] || a;
      const labelB = behaviorTitleByKey[b] || b;
      return labelA.localeCompare(labelB);
    });
    return values.map((value) => ({
      value,
      label: behaviorTitleByKey[value] || value,
    }));
  }, [behaviorTagSets]);

  const filterOptions = React.useMemo(() => ([
    {
      key: 'species',
      label: 'Species',
      type: 'single-select',
      list: speciesOptions,
      getValue: (_value, record) => resolveSpeciesLabel(record?.species),
    },
    {
      key: 'behavior_key',
      label: 'Behavior',
      type: 'single-select',
      list: behaviorOptions,
    },
    {
      key: 'tags',
      label: 'Tag',
      type: 'text',
      getValue: (_value, record) => (record?.tags || [])
        .map((tag) => [tag?.label, tag?.description].filter(Boolean).join(' '))
        .join(' '),
    },
  ]), [behaviorOptions, speciesOptions]);

  const sortOptions = React.useMemo(() => ([
    {
      key: 'species',
      label: 'Species',
      default: 'asc',
      getValue: (_value, record) => resolveSpeciesLabel(record?.species),
    },
    {
      key: 'behavior_key',
      label: 'Behavior',
      getValue: (value) => behaviorTitleByKey[value] || value,
    },
    {
      key: 'tags',
      label: 'Tag Count',
      getValue: (_value, record) => record?.tags?.length || 0,
    },
  ]), []);

  const navigateToSet = (id) => {
    navigate(`/behavior-tags/${id}`);
  };

  const handleOpenMenu = (event, behaviorTagSet) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedBehaviorTagSet(behaviorTagSet);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedBehaviorTagSet(null);
  };

  const handleDeleteBehaviorTagSet = () => {
    const currentBehaviorTagSet = selectedBehaviorTagSet;
    handleCloseMenu();

    if (!currentBehaviorTagSet) return;

    confirm({
      title: 'Delete Behavior Tag Set',
      message: (
        <div>
          <p style={{ margin: 0 }}>
            You are about to delete this behavior tag set.
            <br />
            Are you sure?
          </p>
          <p style={{ color: 'red', margin: 0 }}>
            This action cannot be undone
          </p>
        </div>
      ),
      onSubmit: async () => {
        try {
          await deleteBehaviorTagSet(currentBehaviorTagSet.id, isOnline);
          setSuccess('Deleted behavior tag set');
          revalidator.revalidate();
        } catch (error) {
          console.error(error);
          setError(error);
        }
      },
    });
  };

  const handleImportFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setImportFile(nextFile);
  };

  const handleImportSubmit = async (event) => {
    event.preventDefault();

    if (!importFile) {
      setError('Please choose a CSV file before importing.');
      return;
    }

    const isCsvFile = importFile.name.toLowerCase().endsWith('.csv')
      || importFile.type === 'text/csv'
      || importFile.type === 'application/vnd.ms-excel';

    if (!isCsvFile) {
      setError(`The selected file must be a CSV file. Received: ${importFile.name}`);
      return;
    }

    try {
      const fileContents = await importFile.text();
      const rows = parseCsvText(fileContents);

      if (!rows.length) {
        setError('The CSV file is empty.');
        return;
      }

      const headerRow = rows[0].map((value) => value.trim());
      if (headerRow.length !== requiredImportHeaders.length) {
        setError(
          `The CSV must contain exactly ${requiredImportHeaders.length} columns. Found ${headerRow.length}: ${headerRow.join(', ')}`
        );
        return;
      }

      const missingHeaders = requiredImportHeaders.filter((header) => !headerRow.includes(header));
      if (missingHeaders.length) {
        setError(
          `The CSV is missing required columns: ${missingHeaders.join(', ')}. Found: ${headerRow.join(', ')}`
        );
        return;
      }

      const dataRows = rows.slice(1).map((row, rowIndex) => {
        if (row.length !== requiredImportHeaders.length) {
          throw new Error(
            `Row ${rowIndex + 2} has ${row.length} columns. Expected ${requiredImportHeaders.length}.`
          );
        }

        return headerRow.reduce((acc, header, index) => {
          acc[header] = row[index] || '';
          return acc;
        }, {});
      });

      const groupedRecords = new Map();

      dataRows.forEach((row, rowIndex) => {
        const speciesLabel = row.species;
        const species = speciesValueByLabel[speciesLabel];
        if (!species) {
          throw new Error(
            `Row ${rowIndex + 2} has an invalid species value: ${speciesLabel}`
          );
        }

        if (!validBehaviorKeys.has(row.behavior)) {
          throw new Error(
            `Row ${rowIndex + 2} has an invalid behavior value: ${row.behavior}`
          );
        }

        const tagLabel = (row.tag_label || '').trim();
        const tagDescription = (row.tag_description || '').trim();
        const recordKey = `${species}::${row.behavior}`;

        if (!groupedRecords.has(recordKey)) {
          groupedRecords.set(recordKey, {
            species,
            behavior_key: row.behavior,
            tags: [],
          });
        }

        if (!tagLabel) return;

        groupedRecords.get(recordKey).tags.push({
          label: tagLabel,
          description: tagDescription,
        });
      });

      const recordsToSave = Array.from(groupedRecords.values()).filter((record) => record.tags.length > 0);

      if (!recordsToSave.length) {
        setError('The CSV did not contain any tag rows with a tag_label value to import.');
        return;
      }

      for (const record of recordsToSave) {
        await saveBehaviorTagSet(record, facilityId, isOnline);
      }

      console.log(recordsToSave);
      setSuccess(`Imported ${recordsToSave.length} behavior tag sets`);
      setImportFile(null);
      revalidator.revalidate();
    } catch (error) {
      console.error(error);
      setError(error.message || 'The CSV file could not be parsed.');
    }
  };

  const handleToggleImportExport = () => {
    setIsImportExportOpen((currentValue) => !currentValue);
  };

  const handleDownloadTemplate = () => {
    try {
      const data = allSpecies.flatMap((species) =>
        sectionBBehaviors.map((behavior) => ({
          species,
          behavior: behavior.behaviorKey,
          tag_label: '',
          tag_description: '',
        }))
      );

      downloadCsv({ data, filename: 'behavior-tags-template.csv' });
      setSuccess('Downloaded behavior tag template');
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Header label="Specific Behavior Tags" />
      <div
        onClick={handleToggleImportExport}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggleImportExport();
          }
        }}
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
      >
        <Grid container alignItems="center" spacing={1}>
          <Grid xs>
            <Subheader label="Import/Export" />
          </Grid>
          <Grid>
            <ExpandMoreIcon
              sx={{
                transform: isImportExportOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Grid>
        </Grid>
      </div>

      <Collapse in={isImportExportOpen}>
        <form onSubmit={handleImportSubmit}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Download a template CSV with all valid species and Section B behaviors.
              </Typography>
              <Button type="button" variant="outlined" onClick={handleDownloadTemplate}>
                Download Template CSV
              </Button>
            </Grid>
            <Grid xs={12} sm={8}>
              <FormFileUpload
                label="Behavior tags CSV"
                buttonLabel="Choose file"
                file={importFile}
                accept=".csv,text/csv"
                onChange={handleImportFileChange}
              />
            </Grid>
            <Grid xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button type="submit" variant="contained" fullWidth>
                Import
              </Button>
            </Grid>
          </Grid>
        </form>
      </Collapse>

      <Filter
        records={behaviorTagSets}
        setFilteredRecords={setFilteredBehaviorTagSets}
        filterOptions={filterOptions}
        sortOptions={sortOptions}
        badgeColor="success"
      />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {filteredBehaviorTagSets.map((behaviorTagSet) => (
          <Grid key={behaviorTagSet.id} xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ position: 'relative' }}>
              <IconButton
                aria-label="behavior tag set actions"
                onClick={(event) => handleOpenMenu(event, behaviorTagSet)}
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
              >
                <MoreVertIcon />
              </IconButton>
              <CardActionArea onClick={() => navigateToSet(behaviorTagSet.id)}>
                <CardContent>
                  <Typography variant="h6">
                    {resolveSpeciesLabel(behaviorTagSet.species)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {behaviorTitleByKey[behaviorTagSet.behavior_key] || behaviorTagSet.behavior_key}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {behaviorTagSet.tags?.length || 0} tags
                  </Typography>
                  {behaviorTagSet.tags?.length ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {behaviorTagSet.tags
                        .map((tag) => tag?.label)
                        .filter((value) => !!value)
                        .join(', ')}
                    </Typography>
                  ) : null}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleDeleteBehaviorTagSet}>Delete</MenuItem>
      </Menu>

      <EwpFab onClick={() => navigate('/behavior-tags/new')}>
        <AddIcon />
      </EwpFab>
    </Container>
  );
}
